import { getPackageBucket } from './partnerListUtils';

export const isPartnerPlanLimitError = (error) => {
  const code = error?.response?.data?.code;
  return error?.response?.status === 403 && (
    code === 'partner_trip_limit' ||
    code === 'partner_team_limit' ||
    code === 'partner_traveler_limit' ||
    code === 'partner_ai_allowance' ||
    code === 'partner_ai_limit' ||
    code === 'partner_ai_paywall' ||
    code === 'partner_feature_limit'
  );
};

export const partnerCapacityFromError = (error) => {
  if (!isPartnerPlanLimitError(error)) return null;
  const body = error.response.data || {};
  return {
    code: body.code,
    entitlements: body.entitlements || null,
    recommendation: body.recommendation || null,
    creditPacks: body.creditPacks || null,
    checkoutType: body.checkoutType || null,
    extraNeeded: body.extraNeeded || null,
    requiresUpgrade: Boolean(body.requiresUpgrade),
    used: body.used ?? null,
    limit: body.limit ?? null,
    message: body.error
  };
};

/**
 * AI Concierge gate codes returned by the partner AI endpoints. These are the
 * per-trip limits (cooldown, caps, access) rather than plan entitlements, so
 * they need their own copy instead of the plan capacity dialog.
 */
const AI_GATE_MESSAGE_KEYS = {
  rate_limited: 'partnerAi.limitRateLimited',
  daily_cap: 'partnerAi.limitDailyCap',
  lifetime_cap: 'partnerAi.limitLifetimeCap',
  trip_ended: 'partnerAi.limitTripEnded',
  trip_completed: 'partnerAi.limitTripEnded',
  trip_forbidden: 'partnerAi.limitTripAccess',
  trip_not_found: 'partnerAi.limitTripAccess'
};

export const aiGateMessage = (error, t, fallbackKey = 'partnerAi.generateFailed') => {
  const data = error?.response?.data || {};
  const messageKey = AI_GATE_MESSAGE_KEYS[data.code];
  if (messageKey) return t(messageKey);
  return data.error || t(fallbackKey);
};

export const formatPlanLimit = (limit, unlimitedLabel = 'Unlimited') => {
  if (limit === -1 || limit == null) return unlimitedLabel;
  return String(limit);
};

/** Trip usage line (without seats) for billing metrics. */
export const partnerPlanTripsSummary = (entitlements, t) => {
  if (!entitlements) return '';
  const usage = entitlements.usage || {};
  const limits = entitlements.limits || {};
  const clientLimit = limits.maxClientTrips;
  if (clientLimit != null && clientLimit !== -1 && clientLimit > 0) {
    return t('partnerPlans.clientTripsUsage', {
      used: usage.clientTrips ?? 0,
      limit: clientLimit
    });
  }
  return t('partnerPlans.tripsUncapped');
};

/** Seat and trip usage line for plan wall / billing headers. */
export const partnerPlanUsageSummary = (entitlements, t) => {
  if (!entitlements) return '';
  const usage = entitlements.usage || {};
  const limits = entitlements.limits || {};
  const seatLimit = limits.includedTeamSeats ?? limits.maxTeamMembers;
  const seatsLine = t('partnerPlans.teamSeats', {
    used: usage.teamMembers ?? 1,
    limit: formatPlanLimit(seatLimit, t('partnerPlans.unlimited'))
  });
  return `${seatsLine} · ${partnerPlanTripsSummary(entitlements, t)}`;
};

export const planNameKey = (tier) => {
  if (tier === 'pro') return 'partnerPlans.pro';
  if (tier === 'group') return 'partnerPlans.group';
  if (tier === 'agency') return 'partnerPlans.agency';
  if (tier === 'bespoke') return 'partnerPlans.bespoke';
  return 'partnerPlans.starter';
};

export const billingIntervalFromYearly = (yearly) => (yearly ? 'year' : 'month');

export const normalizeBillingInterval = (interval) => (interval === 'year' ? 'year' : 'month');

export const partnerCheckoutTierType = (plan) => {
  if (!plan?.selfServe) return null;
  if (plan.tierType) return plan.tierType;
  if (plan.free || plan.id === 'free') return 'free';
  return null;
};

export const isCurrentSelfServePlan = (plan, currentTier, currentInterval, selectedInterval) => {
  if ((currentTier || 'free') !== plan.id) return false;
  if (plan.free || !plan.selfServe || !plan.tierType) return true;
  return normalizeBillingInterval(currentInterval) === selectedInterval;
};

export const isScheduledSelfServePlan = (plan, scheduledPlan, selectedInterval) => {
  if (!scheduledPlan?.tier) return false;
  if (scheduledPlan.tier !== plan.id) return false;
  if (plan.free || !plan.selfServe || !plan.tierType) return true;
  return normalizeBillingInterval(scheduledPlan.billingInterval) === selectedInterval;
};

export const partnerPlanCtaLabel = ({ plan, currentTier, currentInterval, yearly, t, name, scheduledPlan }) => {
  const selectedInterval = billingIntervalFromYearly(yearly);
  if (isScheduledSelfServePlan(plan, scheduledPlan, selectedInterval)) {
    return t('partnerPlans.scheduledPlan');
  }
  const isCurrent = isCurrentSelfServePlan(plan, currentTier, currentInterval, selectedInterval);
  if (isCurrent) return t('partnerPlans.currentPlan');
  if (!plan.selfServe) return t('partnerPlans.callUs');
  if ((currentTier || 'free') === plan.id && plan.tierType) {
    return yearly ? t('partnerPlans.switchToYearly') : t('partnerPlans.switchToMonthly');
  }
  return t('partnerPlans.selectPlan', { plan: name });
};

const OVER_LIMIT_KEYS = {
  teamMembers: 'overLimitTeamMembers',
  clientTrips: 'overLimitClientTrips',
  groupTrips: 'overLimitGroupTrips',
  packages: 'overLimitPackages',
  activeTravelers: 'overLimitTravelers'
};

export const formatOverLimitSummary = (overLimit, t) => {
  if (!overLimit || typeof overLimit !== 'object') return '';
  return Object.entries(overLimit)
    .map(([key, value]) => {
      const labelKey = OVER_LIMIT_KEYS[key];
      if (!labelKey) return null;
      return t(`partnerPlans.${labelKey}`, value);
    })
    .filter(Boolean)
    .join(', ');
};

export const canUsePartnerClientInvite = (entitlements) => {
  if (!entitlements) return false;
  if (entitlements.features && Object.prototype.hasOwnProperty.call(entitlements.features, 'hasClientInvite')) {
    return Boolean(entitlements.features.hasClientInvite);
  }
  return String(entitlements.tier || '').toLowerCase() === 'bespoke';
};

export const canUsePartnerWhiteLabel = (entitlements) => {
  if (!entitlements) return false;
  if (entitlements.features && Object.prototype.hasOwnProperty.call(entitlements.features, 'hasWhiteLabel')) {
    return Boolean(entitlements.features.hasWhiteLabel);
  }
  return String(entitlements.tier || '').toLowerCase() === 'bespoke';
};

export {
  isBespokePartnerPlan,
  canCreatePartnerGroupTrips,
  canUseMarketplaceListing,
  canUseFeaturedPlacement,
  canUseGooglePlacesBrowse,
  canUsePartnerAiPlanning,
  canUseBrandedItineraries,
  canUseTeamReporting,
  evaluatePartnerTierGates,
  TIER_GATE_EXPECTATIONS,
  PARTNER_TIER_IDS,
  entitlementsFixture,
} from './partnerTierGates';

export const canInvitePartnerTeamMember = (entitlements) => {
  if (!entitlements) return null;
  const limit = entitlements.limits?.maxTeamMembers;
  const used = entitlements.usage?.teamMembers ?? 0;
  if (limit === -1 || limit == null) return true;
  return used < limit;
};

export const isPackageInPublicCatalog = (pkg, entitlements, now) => {
  if (getPackageBucket(pkg, now) !== 'live') return false;
  if (pkg?.visibility === 'private_client') return false;
  return true;
};

export const isPackageHighlighted = (pkg, entitlements, now) => {
  if (getPackageBucket(pkg, now) !== 'live') return false;
  if (!entitlements?.features?.hasFeaturedPlacement) return false;
  return Boolean(pkg?.is_featured);
};
